<template>
  <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>

  <FlowHeading
    :title="$t('Payment details')"
    title-type="title1"
    :sub-title="$t('To get paid through Worksome you need to add your bank account details.')"
  />
  <BankAccountDetails
    :countries="countries"
    :errors="errors"
    :bank-account="bankAccount"
    @update:form="form = $event"
    @update:valid-country="hasSelectedValidBankCountry = $event"
  />

  <div class="tw-mt-6">
    <WdsButton full-width lg primary :disabled="!canSubmit" @click="update">
      <div class="tw-flex">
        <transition name="fade">
          <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
        </transition>
        {{ $t('Continue') }}
      </div>
    </WdsButton>
  </div>

  <div v-if="lacksRequiredFields" class="tw-mt-4">
    <WdsStack justify-center>
      <WdsButtonGhost secondary @click="skip">{{ $t('Skip and update later') }}</WdsButtonGhost>
    </WdsStack>
  </div>
</template>

<script setup>
import { computed, ref, inject } from 'vue'
import { camelCase, mapKeys } from 'lodash'
import { WdsAlert, WdsStack, WdsButtonGhost, WdsButton } from '@worksome/wds-components'

import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { StoreBankDetailsMutation } from '@/components/GraphQL/mutations/StoreBankDetailsMutation'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import BankAccountDetails from '@/components/BankAccountDetails.vue'
import { FormValidation } from '@/utils/formValidation'
import { isEmpty } from '@/utils/tools'

defineProps({
  bankAccount: {
    type: Object,
    required: true,
  },

  countries: {
    type: Object,
    required: true,
  },
})

const skip = inject('skip')
const next = inject('next')

const isSubmitting = ref(false)

const errors = ref({})
const errorBannerMessage = ref(null)

const form = ref({})
const hasSelectedValidBankCountry = ref(false)

const lacksRequiredFields = computed(() => {
  if (hasSelectedValidBankCountry.value === false) {
    return true
  }

  // Clone the form object
  const formValues = { ...form.value }

  if (Object.keys(formValues).length === 0) {
    return true
  }

  // Delete values we don't want to check, specially the
  // country as it will be usually pre selected, making
  // the form to look like it is filled when it's not.
  delete formValues.bank_country

  return isEmpty(formValues)
})

const canSubmit = computed(() => !isSubmitting.value && !lacksRequiredFields.value)

const resetErrors = () => {
  errors.value = {}
  errorBannerMessage.value = null
}

const update = async () => {
  isSubmitting.value = true

  const bban = mapKeys(form.value.bban, (value, key) => camelCase(key))

  await publicApi
    .mutate({
      mutation: StoreBankDetailsMutation,
      variables: {
        input: {
          accountId: currentAccount.globalId,
          bankAddress: form.value.bank_address,
          bankCountry: form.value.bank_country,
          bankName: form.value.bank_name,
          beneficiaryName: form.value.beneficiary_name,
          bban,
          iban: form.value.iban,
          swift: form.value.swift,
        },
      },
    })
    .then(() => {
      resetErrors()

      next()
    })
    .catch((error) => {
      resetErrors()

      const { graphQLErrors } = error

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }

      isSubmitting.value = false
    })
}
</script>
